import { navigate } from 'gatsby';
import React, { useEffect } from 'react';
import { useSessionStorage } from 'react-use';

import * as ROUTES from '@constants/routes';

import { useSession } from '@hooks/useSession';

import type { FhirResource } from '@models/fhir-resource';

import { getQueryParams } from '@utils/url';

import type { GetVitallPatientRecordQuery } from 'generated/vitall-patient.generated';
import {
  useGetVitallPatientRecordLazyQuery,
  useGetVitallPatientRecordQuery,
} from 'generated/vitall-patient.generated';

interface CurationState {
  isCurating: boolean;
  patientView: boolean;
  vitall_patient_id: string | null;
  vitall_patient: GetVitallPatientRecordQuery['getVitallPatientRecord'] | null;
  isEditing: boolean;
  isCreating: boolean;
  record: FhirResource | null;
}

export enum CurationActions {
  Reset = 'reset',
  StartCuration = 'startCuration',
  StopCuration = 'stopCuration',
  View = 'view',
  StartEdit = 'start-edit',
  StopEdit = 'stopEdit',
  StartCreating = 'startCreating',
  FinishCreating = 'finishCreating',
  SetRecord = 'setRecord',
  RestoreState = 'restoreState',
}

type Payload = {
  [CurationActions.Reset]: undefined;
  [CurationActions.StartCuration]: {
    record: FhirResource | null;
  };
  [CurationActions.StopCuration]: undefined;
  [CurationActions.View]: {
    vitall_patient_id: string;
  };
  [CurationActions.StartEdit]: undefined;
  [CurationActions.StopEdit]: undefined;
  [CurationActions.StartCreating]: undefined;
  [CurationActions.FinishCreating]: undefined;
  [CurationActions.SetRecord]: {
    record: FhirResource | null;
  };
  [CurationActions.RestoreState]: {
    state: CurationState;
  };
};

const initialCurationState: CurationState = {
  /** Viewing a patient's record drilldown */
  isCurating: false,
  /** View as a patient */
  patientView: false,
  /** Curator is editing a record */
  isEditing: false,
  /** Currently viewed patient */
  vitall_patient_id: null,
  vitall_patient: null,
  /** Curator is adding a new record */
  isCreating: false,
  /** Currently viewed record */
  record: null,
};

function reducer(state: CurationState, action: Action<Payload>): CurationState {
  switch (action.type) {
    case CurationActions.Reset:
      return initialCurationState;
    case CurationActions.StartCuration:
      return {
        ...state,
        isCurating: true,
        record: action.record,
      };
    case CurationActions.StopCuration:
      return {
        ...state,
        isCurating: false,
        record: null,
        isEditing: false,
        isCreating: false,
      };
    case CurationActions.View:
      return {
        ...state,
        patientView: true,
        vitall_patient_id: action.vitall_patient_id,
      };
    case CurationActions.StartEdit:
      return { ...state, isEditing: true };
    case CurationActions.StopEdit:
      return { ...state, isEditing: false };
    case CurationActions.StartCreating:
      return { ...state, isCreating: true };
    case CurationActions.FinishCreating:
      return { ...state, isCreating: false };
    case CurationActions.SetRecord:
      return { ...state, record: action.record };
    case CurationActions.RestoreState:
      return { ...state, ...action.state };
    default:
      return { ...state };
  }
}

export const CurationContext = React.createContext<{
  state: CurationState;
  dispatch: React.Dispatch<Action<Payload>>;
}>({ state: initialCurationState, dispatch: () => null });
CurationContext.displayName = 'CurationContext';

export const CurationProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const {
    state: {
      isAuthenticated,
      isCurator,
      isLoadingAuth,
      isInitialized,
      isPractitioner,
      active_user_id,
      logged_in_user_id,
    },
    setActiveProfile,
    resetActiveProfile,
  } = useSession();

  const [getVitallPatient] = useGetVitallPatientRecordLazyQuery();

  const [storedState, setStoredState] = useSessionStorage<CurationState>(
    'curation:state',
    initialCurationState
  );
  const [clientId, setClientId] = React.useState<string>();
  const [state, dispatch] = React.useReducer(reducer, storedState);

  useEffect(() => {
    const { client_id } = getQueryParams();
    if (typeof client_id === 'string') {
      setClientId(client_id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setStoredState(state);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  useEffect(() => {
    async function handle() {
      if (
        isInitialized &&
        clientId &&
        isAuthenticated &&
        (isCurator || isPractitioner)
      ) {
        const { data } = await getVitallPatient({
          variables: {
            input: { user_id: clientId },
            is_curator: isCurator,
            is_practitioner: isPractitioner,
          },
        });
        const vitall_patient = data?.getVitallPatientRecord;
        const user_id = data?.getVitallPatientRecord?.user_id;
        if (vitall_patient && user_id) {
          const newState: CurationState = {
            ...state,
            patientView: true,
            vitall_patient_id: vitall_patient.vitall_patient_id,
            vitall_patient,
          };
          setActiveProfile(user_id);
          dispatch({
            type: CurationActions.RestoreState,
            state: newState,
          });
          setClientId(undefined);
        }
      } else if (isInitialized && !isAuthenticated && !isLoadingAuth) {
        dispatch({
          type: CurationActions.Reset,
        });
      }
    }
    handle();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId, isAuthenticated, isCurator]);

  React.useEffect(() => {
    if (
      state.patientView &&
      logged_in_user_id === active_user_id &&
      isInitialized &&
      isAuthenticated &&
      !isLoadingAuth
    ) {
      resetActiveProfile();
      dispatch({ type: CurationActions.Reset });
      navigate(ROUTES.DASHBOARD);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    state.patientView,
    active_user_id,
    logged_in_user_id,
    isInitialized,
    isAuthenticated,
    isLoadingAuth,
  ]);

  const { data: vitallPatientData } = useGetVitallPatientRecordQuery({
    variables: {
      input: { vitall_patient_id: state.vitall_patient_id ?? '' },
      is_curator: isCurator,
      is_practitioner: isPractitioner,
    },
    skip: !state.vitall_patient_id || !isAuthenticated,
    fetchPolicy: 'cache-first',
  });
  const stateKey = JSON.stringify(state);
  const patientKey = JSON.stringify(vitallPatientData?.getVitallPatientRecord);
  const value = React.useMemo(
    () => ({
      state: {
        ...state,
        vitall_patient: vitallPatientData?.getVitallPatientRecord,
      },
      dispatch,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stateKey, patientKey]
  );

  return (
    <CurationContext.Provider value={value}>
      {children}
    </CurationContext.Provider>
  );
};
